/**
 * AI-First Device Analyzer
 *
 * Uses AI-based approach as primary method with pattern-matching as fallback
 */

import { AnalysisContext, DeviceInfo } from "../types";
import { BaseDeviceAnalyzer } from "./baseAnalyzer";
import { GenericDeviceAnalyzer } from "./genericAnalyzer";
import { DeviceInfoExtractionService } from "../../gemini/deviceInfoExtraction";
import { AIModel, getAIServiceConfig } from "../../gemini/config";

/**
 * AI-First Device Analyzer
 *
 * This analyzer uses AI as the primary extraction method,
 * with pattern-matching as a fallback for critical fields
 */
export class AIDeviceAnalyzer extends BaseDeviceAnalyzer {
  private genericAnalyzer = new GenericDeviceAnalyzer();
  private aiService: DeviceInfoExtractionService;

  // Configuration values
  private confidenceThreshold: number;

  /**
   * Creates a new AI-First Device Analyzer
   * @param model AI model to use (optional)
   */
  constructor(model: AIModel = AIModel.GEMINI) {
    super();
    const config = getAIServiceConfig();
    this.confidenceThreshold = config.confidenceThreshold;

    // Get the API key from environment variables
    const apiKey = process.env.REACT_APP_GOOGLE_API_KEY;

    if (!apiKey) {
      console.error("REACT_APP_GOOGLE_API_KEY is not defined");
    }

    // Initialize the AI service with the API key
    this.aiService = new DeviceInfoExtractionService(apiKey || "", model);
  }

  /**
   * Set a high priority for the hybrid analyzer
   */
  getPriority(): number {
    return 95; // Higher than all other analyzers
  }

  /**
   * Checks if this analyzer can handle the given context
   * @param context The analysis context
   * @returns True if this analyzer can handle the context
   */
  canAnalyze(context: AnalysisContext): boolean {
    // The AI-first analyzer can handle any context
    return true;
  }

  /**
   * Analyzes the context to extract device information using an AI-first approach
   * @param context The analysis context
   * @returns The extracted device information
   */
  analyze(context: AnalysisContext): Partial<DeviceInfo> {
    // Start with empty result - will be populated asynchronously
    const initialResult: Partial<DeviceInfo> = {
      confidence: 0,
    };

    // Store the result in the context so it can be accessed by other components
    context.deviceInfo = initialResult;

    // Trigger the async analysis in the background
    this.analyzeAsync(context)
      .then((result) => {
        // Update the context with the async results
        Object.assign(context.deviceInfo || {}, result);
      })
      .catch((error) => {
        console.error("Error in async analysis:", error);
      });

    return initialResult;
  }

  /**
   * Analyzes device information using AI and pattern matching (async version)
   * @param context The analysis context
   * @returns The extracted device information
   */
  private async analyzeAsync(
    context: AnalysisContext
  ): Promise<Partial<DeviceInfo>> {
    try {
      // Try AI extraction first as the primary method
      const aiResults = await this.extractDeviceInfoFromAI(context.text);

      if (aiResults) {
        // If AI results are available, use them directly
        const result = {
          ...aiResults,
          confidence: aiResults.confidence
            ? Math.round(aiResults.confidence * 100)
            : 70,
        };

        return result;
      }

      // Fall back to pattern matching if AI extraction fails or has low confidence
      console.log("Falling back to pattern matching");
      const genericResults = this.genericAnalyzer.analyze(context);

      return genericResults;
    } catch (error) {
      console.error("Error in AI analyzer:", error);
      // Fall back to pattern matching if there's an error
      return this.genericAnalyzer.analyze(context);
    }
  }

  /**
   * Get enhanced results with AI (async)
   * This method returns a Promise that resolves when AI extraction is complete
   * @param context The analysis context
   * @returns The enhanced device information
   */
  async getEnhancedResults(
    context: AnalysisContext
  ): Promise<Partial<DeviceInfo>> {
    try {
      // Try AI extraction
      const aiResults = await this.extractDeviceInfoFromAI(context.text);

      if (!aiResults) {
        // Return the original results if AI extraction fails
        return context.deviceInfo || {};
      }

      // Merge the AI results with the original results
      const enhancedResults = {
        ...(context.deviceInfo || {}),
        ...aiResults,
        confidence: aiResults.confidence
          ? Math.round(aiResults.confidence)
          : 70,
        aiEnhanced: true,
      };

      return enhancedResults;
    } catch (error) {
      console.error("Error in AI enhancement:", error);
      // Return the original results if there's an error
      return context.deviceInfo || {};
    }
  }

  /**
   * Extracts device information from text using AI
   * @param text The text to analyze
   * @returns The extracted device information
   */
  private async extractDeviceInfoFromAI(
    text: string
  ): Promise<Partial<DeviceInfo> | undefined> {
    try {
      // Use the AI service to extract device information
      const result = await this.aiService.extractDeviceInfo(text);

      if (!result) {
        console.log("AI extraction failed or returned no results");
        return undefined;
      }

      // Check if the overall confidence is above the threshold
      if (result.confidence.overall < this.confidenceThreshold * 100) {
        console.log(
          `AI extraction confidence (${result.confidence.overall
          }) below threshold (${this.confidenceThreshold * 100})`
        );
        return undefined;
      }

      // Mark the result as AI-enhanced
      result.deviceInfo.aiEnhanced = true;

      // Handle virtual machine detection
      if (
        (result.deviceInfo.make?.toLowerCase().includes("vmware") ||
          result.deviceInfo.systemManufacturer
            ?.toLowerCase()
            .includes("vmware")) &&
        !result.deviceInfo.type
      ) {
        result.deviceInfo.type = "unknown";
      }

      return result.deviceInfo;
    } catch (error) {
      console.error("Error in AI extraction:", error);
      return undefined;
    }
  }

  /**
   * Checks if the AI results have high confidence
   * @param aiResults The AI extraction results
   * @returns Whether the results have high confidence
   */
  private hasHighConfidence(aiResults: any): boolean {
    // Check overall confidence
    if (aiResults.confidence.overall >= this.confidenceThreshold) {
      return true;
    }

    // Check confidence for critical fields
    const criticalFields = ["make", "model", "osType", "serialNumber"];
    const criticalFieldsConfidence = criticalFields.map(
      (field) => aiResults.confidence[field] || 0
    );

    // If average confidence for critical fields is high enough, consider it high confidence
    const avgCriticalConfidence =
      criticalFieldsConfidence.reduce((a, b) => a + b, 0) /
      criticalFieldsConfidence.length;
    return avgCriticalConfidence >= this.confidenceThreshold;
  }

  /**
   * Gets results from pattern-matching analyzers
   * @param context The analysis context
   * @returns The extracted device information
   */
  private getPatternMatchingResults(
    context: AnalysisContext
  ): Partial<DeviceInfo> {
    // Use generic analyzer for basic pattern matching
    return this.genericAnalyzer.analyze(context);
  }

  /**
   * Merges AI results with pattern-matching results
   * @param aiResults Results from AI extraction
   * @param patternResults Results from pattern-matching extraction
   * @returns Merged results
   */
  private mergeResults(
    aiResults: {
      deviceInfo: Partial<DeviceInfo>;
      confidence: {
        overall: number;
        osType?: number;
        osVersion?: number;
        serialNumber?: number;
        year?: number;
        model?: number;
        make?: number;
      };
    },
    patternResults: Partial<DeviceInfo>
  ): Partial<DeviceInfo> {
    // Start with AI results as the base
    const result: Partial<DeviceInfo> = { ...aiResults.deviceInfo };

    // Define required fields that should be prioritized
    const requiredFields = [
      "type",
      "make",
      "model",
      "serialNumber",
      "osType",
      "osVersion",
    ];

    // For any required fields missing in AI results, use pattern matching results if available
    requiredFields.forEach((field) => {
      const fieldName = field as keyof DeviceInfo;
      if (!result[fieldName] && patternResults[fieldName]) {
        result[fieldName] = patternResults[fieldName] as any;
      }
    });

    // For any other fields not in AI results, use pattern matching results
    Object.entries(patternResults).forEach(([key, value]) => {
      const fieldName = key as keyof DeviceInfo;
      if (!result[fieldName] && value !== undefined) {
        result[fieldName] = value as any;
      }
    });

    // Ensure we have a high confidence value
    result.confidence = Math.max(
      result.confidence || 0,
      Math.round(aiResults.confidence.overall * 100),
      patternResults.confidence || 0
    );

    // Mark as AI enhanced
    result.aiEnhanced = true;

    return result;
  }
}
